import { AlertCircle } from "lucide-react";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SERVER_URL } from "../api/client";
import { BrandMark } from "../components/layout/BrandMark";
import { useAuth } from "../context/AuthContext";

const errorMessages: Record<string, string> = {
	domain: "",
	auth_failed: "Authentication failed. Please try again.",
};

const Login = () => {
	const { authenticated, loading } = useAuth();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const error = searchParams.get("error");

	useEffect(() => {
		if (!loading && authenticated) {
			navigate("/dashboard", { replace: true });
		}
	}, [authenticated, loading, navigate]);

	const handleGoogleLogin = () => {
		window.location.href = `${SERVER_URL}/auth/google`;
	};

	if (loading) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-brand-night">
				<div className="h-10 w-10 animate-spin rounded-full border-4 border-white/10 border-t-brand" />
			</div>
		);
	}

	return (
		<div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-brand-night bg-[radial-gradient(ellipse_at_top,rgba(250,204,21,0.08),transparent_60%)]">
			<div className="relative z-10 w-full max-w-md px-6">
				{/* Card */}
				<div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8">
					{/* Logo */}
					<div className="mb-8 flex flex-col items-center border-white/10 border-b pb-8 text-[#e7e8e3]">
						<BrandMark />
						<h1 className="sr-only">PriceOnline</h1>
					</div>

					{/* Welcome text */}
					<div className="mb-8">
						<p className="font-semibold text-[0.7rem] text-brand uppercase tracking-[0.18em]">
							Admin · Restricted access
						</p>
						<h2 className="mt-3 font-semibold text-[26px] text-[#e7e8e3] leading-tight">
							Welcome back.{" "}
							<span className="text-zinc-500">Sign in to continue.</span>
						</h2>
						<p className="mt-3 text-[13px] text-zinc-400">
							Use your PriceOnline Google account.
						</p>
					</div>

					{/* Error message */}
					{error && (
						<div className="mb-6 flex items-center gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-4">
							<AlertCircle className="h-5 w-5 shrink-0 text-red-400" />
							<p className="text-red-300 text-sm">
								{errorMessages[error] || "An unexpected error occurred."}
							</p>
						</div>
					)}

					{/* Google Sign-in Button */}
					<button
						type="button"
						onClick={handleGoogleLogin}
						className="flex w-full cursor-pointer items-center justify-center gap-3 rounded-full bg-white px-6 py-3 font-semibold text-[13px] text-zinc-900 transition-colors duration-200 hover:bg-zinc-200 active:scale-[0.99]"
					>
						<svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
							<title>Google</title>
							<path
								fill="#4285F4"
								d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
							/>
							<path
								fill="#34A853"
								d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
							/>
							<path
								fill="#FBBC05"
								d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
							/>
							<path
								fill="#EA4335"
								d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
							/>
						</svg>
						Sign in with Google
					</button>
				</div>

				{/* Footer */}
				<p className="mt-8 text-center text-[11px] text-zinc-600">
					© {new Date().getFullYear()} PriceOnline. All rights reserved.
				</p>
			</div>
		</div>
	);
};

export default Login;
